import classNames from 'classnames'
import { useEffect, useState } from 'react'

import { useMyMonitoringPingUnit } from '@apps-common/apis/my-monitoring/control'
import { useUnitControlProgram } from '@apps-common/apis/portal/control'
import {
  AccountStatus,
  ConnectivityStatus,
  PingButton,
  ProductStatus,
  SignalStatus
} from '@apps-common/apps/portal/pages/units/pages/manage/components'
import { VertSpacerMd, VertSpacerSm } from '@apps-common/components/layout'
import { ModalBody, ModalContainer } from '@apps-common/components/modals/modal'
import { MessageProgramControl } from '@apps-common/config/app-messages'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { toastError, toastSuccess } from '@apps-common/lib/toast'
import { IMMSiteModel } from '@apps-common/types/my-monitoring'
import {
  DeviceStatus,
  OUTPUT_CLOSED,
  OUTPUT_OPEN,
  SENSOR_HIGH_ALARM,
  SENSOR_HIGH_RESTORE,
  SENSOR_LOW_ALARM,
  SENSOR_LOW_RESTORE
} from '@apps-common/utils/device-status'

export interface IStatusInfoModal {
  site: IMMSiteModel
}

export const StatusInfoMyMonitoringModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: IStatusInfoModal
}) => {
  const { site } = componentProps
  const [status, setStatus] = useState<DeviceStatus | undefined>()
  const { mutate: restartDevice } = useUnitControlProgram(
    false,
    () => toastSuccess(MessageProgramControl.RestartSuccess),
    () => toastError(MessageProgramControl.RestartFailure)
  )
  // const { open: openHistory } = useHistoryModal({ type: 'program', name: unit.siteName || unit.accountNo, id: 0, unitId: unit.unitId })
  // const isCancelled = unitIsCancelled(unit)

  const { isLoading, isFetching, isRefetching, isError, data, refetch } = useMyMonitoringPingUnit({
    config: {
      enabled: !!site?.unitId
    },
    model: {
      unitId: site?.unitId || '',
      triggerType: '',
      onTime: 0,
      commands: []
    }
  })

  useEffect(() => {
    if (data) {
      if (!data.pingResult) {
        refetch()
      } else {
        const statusObj = new DeviceStatus(site.accountNo, site.productId, site.product, site.productImageUrl, data)

        setStatus(statusObj)
      }
    }
  }, [data])

  const isInactive = false
  const isActive = true
  const isCancelled = false

  const loading = !site || isLoading || isFetching || isRefetching

  return (
    <ModalContainer
      innerClasses='!bg-surface-2/70 !backdrop-blur-md !shadow-sm !max-w-[600px] p-0 !h-auto !min-h-0'
      containerClasses='!items-center !justify-center'
      onDismiss={onDismiss}
    >
      {/* <ModalHeaderContainer>
        <div className='flex items-center justify-between pl-1'>
          <ModalHeader title='Device status' />
          <div className='flex items-center justify-end'>
            <ModalCloseButton closeModal={() => onDismiss(null, ModalDismiss.Cancel)} />
          </div>
        </div>
      </ModalHeaderContainer> */}
      <ModalBody classes='!pt-[10px] !pb-[20px]'>
        <div className='w-full !flex !items-center !justify-center'>
          <div className='w-full'>
            <VertSpacerSm />
            <div className='flex flex-col gap-[15px] md:flex-row mx-auto'>
              <div className='grow flex flex-col'>
                <div className='relative flex items-center justify-between sm:px-[10px] pb-4'>
                  <div className='flex items-center justify-between sm:gap-[6px]'>
                    <ProductStatus
                      loading={loading}
                      data={status?.model || { imageUrl: site?.productImageUrl || '', name: site?.product || '' }}
                    />
                    <AccountStatus loading={loading} isInactive={isInactive} data={status?.accountNo || site?.accountNo} />
                    {/* <ActivationStatus loading={loading} isInactive={isInactive} isActive={isActive} isCancelled={isCancelled} /> */}
                    <ConnectivityStatus loading={loading} data={status?.online} />
                    <SignalStatus loading={loading} data={status?.signal} />
                  </div>
                  {/* <div className='flex items-center justify-center p-[5px] gap-[5px] bg-surface border border-border rounded-full'> */}
                  <div className='ml-2 mr-2 h-full flex items-center justify-center gap-2 sm:gap-2'>
                    {/* <div className='block sm:hidden bg-text/[0.075] w-[2px] h-[40px] rounded-full' /> */}
                    {/* <div className='flex items-center justify-start gap-[5px] bg-surface border border-border p-[2px] sm:p-[4px] rounded-full'> */}
                    <PingButton
                      refetch={refetch}
                      requestModel={{
                        unitId: site?.unitId || '',
                        triggerType: '',
                        onTime: 0,
                        commands: []
                      }}
                      disabled={loading}
                      classes='bg-ctx-mm-brand text-ctx-on-mm-brand'
                    />
                    <button
                      type='button'
                      className={classNames(
                        'bg-ctx-mm-brand-2 text-ctx-on-mm-brand-2 rounded-full min-w-[35px] min-h-[35px] sm:min-w-[37px] sm:min-h-[37px] flex items-center justify-center disabled:opacity-60'
                      )}
                      disabled={false}
                      onClick={(e) => {
                        e?.stopPropagation()
                        onDismiss(null, ModalDismiss.Cancel)
                      }}
                    >
                      <commonIcons.close className='text-[1.4rem]' />
                    </button>
                  </div>

                  {/* </div> */}
                </div>
                <div className='flex flex-col sm:flex-row items-stretch justify-between gap-4 grow'>
                  {loading && (
                    <>
                      <div className='w-full min-w-[260px] min-h-[140px] grow bg-skeleton rounded-[25px] px-[15px] py-[12px] animate-pulse flex flex-col gap-2'>
                        <div className='flex items-center justify-between'>
                          <div className='w-[50%] h-[24px] rounded-full bg-skeleton' />
                          <div className='w-[28px] h-[24px] rounded-full bg-skeleton' />
                        </div>
                        <div className='w-full h-[28px] rounded-full bg-skeleton-hi' />
                        <div className='w-full h-[28px] rounded-full bg-skeleton-hi' />
                        <div className='w-full h-[28px] rounded-full bg-skeleton-hi' />
                      </div>
                      <div className='w-full min-w-[260px] grow bg-skeleton rounded-[25px] px-[15px] py-[12px] animate-pulse flex flex-col gap-2'>
                        <div className='flex items-center justify-between'>
                          <div className='w-[50%] h-[24px] rounded-full bg-skeleton' />
                          <div className='w-[28px] h-[24px] rounded-full bg-skeleton' />
                        </div>
                        <div className='w-full h-[28px] rounded-full bg-skeleton-hi' />
                      </div>
                    </>
                  )}

                  {!loading && (
                    <>
                      <div
                        className={classNames(
                          'w-full bg-surface border border-border rounded-[25px] px-[5px] py-[7.5px]',
                          isRefetching && 'opacity-40'
                        )}
                      >
                        <div className='text-text jost text-[0.8em] px-[15px] pt-[10px] flex items-center justify-between'>
                          Outputs <commonIcons.outputSwitch className='text-[1.3em]' />
                        </div>
                        <VertSpacerMd />
                        <div className='text-text-muted jost text-[0.75em] px-[10px] pb-[5px]'>
                          {status?.outputs &&
                            status?.outputs?.length > 0 &&
                            status?.outputs?.map((o, i) => (
                              <div
                                key={`unit-status-output-${i}`}
                                className={classNames(
                                  'flex items-center justify-between py-[5px] px-[10px] rounded-[10px]',
                                  i % 2 === 0 && 'bg-surface-2'
                                )}
                              >
                                <div className='jost-bold'>{o.index + 1}</div>
                                <div
                                  className={classNames(
                                    'flex items-center justify-between uppercase px-[6px] rounded-[8px] w-[52px]',
                                    o.state === OUTPUT_OPEN && 'text-on-error bg-error',
                                    o.state === OUTPUT_CLOSED && 'text-on-success bg-success'
                                  )}
                                >
                                  {o.state} {o.state === OUTPUT_OPEN && <commonIcons.powerOffAlt className='text-[1.1em]' />}
                                  {o.state === OUTPUT_CLOSED && <commonIcons.powerAlt className='text-[1.1em]' />}
                                </div>
                              </div>
                            ))}
                          {!loading && (!status?.outputs || status?.outputs?.length === 0) && (
                            <div className='ml-[5px]'>No output data</div>
                          )}
                        </div>
                      </div>

                      <div
                        className={classNames(
                          'w-full bg-surface border border-border rounded-[25px] px-[5px] py-[7.5px]',
                          isRefetching && 'opacity-40'
                        )}
                      >
                        <div className='text-text jost text-[0.8em] px-[15px] pt-[10px] flex items-center justify-between'>
                          Sensors <commonIcons.temp className='text-[1.45em]' />
                        </div>
                        <VertSpacerMd />
                        <div className='text-text-muted jost text-[0.8em] px-[10px] pb-[5px]'>
                          {status?.sensors &&
                            status?.sensors?.length > 0 &&
                            status?.sensors?.map((s, i) => (
                              <div
                                key={`unit-status-sensor-${i}`}
                                className={classNames(
                                  'flex items-center justify-between py-[5px] px-[10px] rounded-[10px]',
                                  i % 2 === 0 && 'bg-surface-2'
                                )}
                              >
                                <div className='uppercase'>
                                  {s.index + 1}: <span className='text-[0.9em]'>{s?.status?.status}</span>
                                </div>
                                <div className='flex items-center justify-center gap-[10px]'>
                                  <div className='flex items-center justify-center'>
                                    <commonIcons.arrowRight className='rotate-[90deg] text-[1.1em]' />
                                    <div
                                      className={classNames(
                                        'w-[12px] h-[12px] rounded-[6px]',
                                        s?.status?.lowAlarm === SENSOR_LOW_ALARM && 'bg-error',
                                        s?.status?.lowAlarm === SENSOR_LOW_RESTORE && 'bg-success'
                                      )}
                                    />
                                  </div>
                                  <div className='flex items-center justify-center'>
                                    <commonIcons.arrowRight className='rotate-[-90deg] text-[1.1em]' />
                                    <div
                                      className={classNames(
                                        'w-[12px] h-[12px] rounded-[6px]',
                                        s?.status?.lowAlarm === SENSOR_HIGH_ALARM && 'bg-error',
                                        s?.status?.highAlarm === SENSOR_HIGH_RESTORE && 'bg-success'
                                      )}
                                    />
                                  </div>
                                  <div className='flex items-center justify-center'>
                                    {s.value}
                                    <commonIcons.tempCelsius className='text-[1.35em]' />
                                  </div>
                                </div>
                              </div>
                            ))}
                          {!loading && (!status?.sensors || status?.sensors?.length === 0) && (
                            <div className='ml-[5px]'>No sensor data</div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </ModalBody>
    </ModalContainer>
  )
}
